import React, { useState, useEffect } from 'react';
import { useChat } from '../contexts/ChatContext';
import ChatList from '../components/Chat/ChatList';
import ChatInterface from '../components/Chat/ChatInterface';
import StashitLogo from '../components/Logo/StashitLogo';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ChatPage: React.FC = () => {
  const { fetchChatRooms, chatRooms } = useChat();
  const [selectedErrandId, setSelectedErrandId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Request notification permission and fetch chat rooms
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchChatRooms();

      // Request notification permission for new messages
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          console.log('Notification permission:', permission);
        });
      }
    }

    // Listen for message sent events to refresh chat rooms
    const handleMessageSent = () => {
      console.log('Message sent, refreshing chat rooms...');
      fetchChatRooms();
    };

    // Listen for new message notifications
    const handleNewMessage = (event: CustomEvent) => {
      const { errandId, message, sender } = event.detail;

      // Show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`New message from ${sender}`, {
          body: message.length > 50 ? `${message.substring(0, 50)}...` : message,
          icon: '/stashit-icon.svg',
          tag: `chat-${errandId}`
        });
      }

      // Refresh chat rooms to show new message indicator
      fetchChatRooms();
    };

    window.addEventListener('messageSent', handleMessageSent);
    window.addEventListener('newMessage', handleNewMessage as EventListener);

    return () => {
      window.removeEventListener('messageSent', handleMessageSent);
      window.removeEventListener('newMessage', handleNewMessage as EventListener);
    };
  }, [fetchChatRooms]);

  // Handle URL parameters for direct chat access
  useEffect(() => {
    const errandParam = searchParams.get('errand');
    if (errandParam) {
      setSelectedErrandId(errandParam);
    }
  }, [searchParams]);

  const handleSelectChat = (errandId: string) => {
    setSelectedErrandId(errandId);
    // Update URL with errand parameter
    navigate(`/chat?errand=${errandId}`, { replace: true });
  };

  const handleCloseChat = () => {
    setSelectedErrandId(null);
    // Remove errand parameter from URL
    navigate('/chat');
  };

  // Check if user is authenticated by checking for JWT token
  const token = localStorage.getItem('token');
  if (!token) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Please log in to access chat</h1>
          <p className="text-neutral-600 mb-4">Chat functionality requires authentication</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-hidden">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 gap-4">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
              <StashitLogo width={120} height={36} showTagline={false} className="flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Messages</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {chatRooms.length} conversation{chatRooms.length !== 1 ? 's' : ''}
                  {selectedErrandId && (
                    <span className="ml-2 text-blue-600 hidden sm:inline">
                      • Active: {chatRooms.find(r => r.errand_id === selectedErrandId)?.errand_title || `Errand #${selectedErrandId.slice(0, 8)}`}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <div className="text-right hidden sm:block">
                <div className="text-sm text-gray-500">
                  Real-time communication
                </div>
                <div className="text-xs text-gray-400">
                  {new Date().toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => navigate('/')}
                className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">← Back to Dashboard</span>
                <span className="sm:hidden">← Back</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 overflow-x-hidden">
        {isMobile ? (
          // Mobile view - show either list or chat
          <div className="h-[calc(100vh-180px)] min-h-[500px] overflow-x-hidden">
            {selectedErrandId ? (
              <div className="h-full flex flex-col">
                <ChatInterface
                  errandId={selectedErrandId}
                  errandTitle={chatRooms.find(r => r.errand_id === selectedErrandId)?.errand_title || `Errand #${selectedErrandId.slice(0, 8)}`}
                  onClose={handleCloseChat}
                />
              </div>
            ) : (
              <ChatList
                onSelectChat={handleSelectChat}
                selectedErrandId={selectedErrandId || undefined}
              />
            )}
          </div>
        ) : (
          // Desktop view - show both side by side
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 h-[calc(100vh-180px)] overflow-x-hidden">
            {/* Chat List Sidebar */}
            <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 overflow-y-auto overflow-x-hidden">
              <ChatList
                onSelectChat={handleSelectChat}
                selectedErrandId={selectedErrandId || undefined}
              />
            </div>

            {/* Chat Interface */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden overflow-x-hidden">
              {selectedErrandId ? (
                <ChatInterface
                  errandId={selectedErrandId}
                  errandTitle={chatRooms.find(r => r.errand_id === selectedErrandId)?.errand_title || `Errand #${selectedErrandId.slice(0, 8)}`}
                />
                             ) : (
                 <div className="flex flex-col items-center justify-center h-full text-gray-500">
                   <div className="w-24 h-24 mx-auto mb-4 text-gray-300">
                     <svg fill="currentColor" viewBox="0 0 24 24">
                       <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                     </svg>
                   </div>
                   <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                   <p className="text-sm text-center mb-4">
                     Choose an errand from the left to start chatting
                   </p>
                   {chatRooms.length === 0 && (
                     <div className="text-center">
                       <p className="text-sm text-gray-400 mb-2">No conversations yet?</p>
                       <button
                         onClick={() => navigate('/errand-request')}
                         className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                       >
                         Create Your First Errand
                       </button>
                     </div>
                   )}
                 </div>
               )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Navigation Hint */}
      {isMobile && selectedErrandId && (
        <div className="fixed bottom-4 left-4 right-4 bg-blue-600 text-white p-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center justify-between">
            <span className="text-sm truncate mr-2">
              {chatRooms.find(r => r.errand_id === selectedErrandId)?.errand_title || `Errand #${selectedErrandId.slice(0, 8)}`}
            </span>
            <button
              onClick={handleCloseChat}
              className="text-white hover:text-blue-100 transition-colors flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
